import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import { ACCEPTED_VIDEO_EXTENSIONS, ACCEPTED_VIDEO_MIME_TYPES } from '@ddp/shared';
import type {
  Campaign,
  ExtractedImage,
  FieldSession,
  OrgRole,
  ShareLink,
  VideoListItem,
  VideoStatus,
} from '@ddp/shared';
import FileUploaderDropContainer from '@carbon/react/es/components/FileUploader/FileUploaderDropContainer';
import ProgressBar from '@carbon/react/es/components/ProgressBar/ProgressBar';
import Tag from '@carbon/react/es/components/Tag/Tag';
import Button from '@carbon/react/es/components/Button';
import Modal from '@carbon/react/es/components/Modal/Modal';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import TextArea from '@carbon/react/es/components/TextArea/TextArea';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import Checkbox from '@carbon/react/es/components/Checkbox/Checkbox';
import RadioButtonGroup from '@carbon/react/es/components/RadioButtonGroup/RadioButtonGroup';
import RadioButton from '@carbon/react/es/components/RadioButton/RadioButton';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import TrashCan from '@carbon/icons-react/es/TrashCan';
import Share from '@carbon/icons-react/es/Share';
import Copy from '@carbon/icons-react/es/Copy';
import Download from '@carbon/icons-react/es/Download';
import Checkmark from '@carbon/icons-react/es/Checkmark';
import Edit from '@carbon/icons-react/es/Edit';
import Folders from '@carbon/icons-react/es/Folders';
import FolderMoveTo from '@carbon/icons-react/es/FolderMoveTo';
import FolderAdd from '@carbon/icons-react/es/FolderAdd';
import Add from '@carbon/icons-react/es/Add';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { VideoThumbnail } from '../features/video/VideoThumbnail.js';
import { useVisibleCount } from '../hooks/use-visible-count.js';
import { useMultipartUpload } from '../features/upload/use-multipart-upload.js';
import { useVideos } from '../features/videos/use-videos.js';
import { useFieldSessions } from '../features/field-sessions/use-field-sessions.js';
import { useCaptureAssets, type CaptureAsset } from '../features/hardware/use-capture-assets.js';
import { useCampaigns } from '../features/campaigns/use-campaigns.js';
import { useT } from '../i18n/index.js';
import { PageHeader } from '../components/PageHeader.js';
import { HelpHint } from '../components/HelpHint.js';
import { SimulationSection } from '../features/simulation/SimulationSection.js';
import { AddToProjectModal } from '../features/projects/AddToProjectModal.js';
import { ProjectsPanel } from '../features/projects/ProjectsPanel.js';

type TagType = 'blue' | 'green' | 'red' | 'gray';

/** Hiérarchie des rôles (PRD §2.1) : sert à masquer les actions qu'un rôle ne peut pas exercer. */
const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

/** Couleur du statut de traitement (le libellé est traduit au rendu, cf. `statusLabels`). */
const STATUS_META: Record<VideoStatus, { type: TagType }> = {
  PENDING: { type: 'gray' },
  UPLOADING: { type: 'blue' },
  UPLOADED: { type: 'blue' },
  PROCESSING: { type: 'blue' },
  READY: { type: 'green' },
  FAILED: { type: 'red' },
};

/**
 * Accueil authentifié : téléversement direct d'une vidéo et liste des vidéos de l'organisation.
 *
 * L'octet ne transite jamais par l'API : le hook `useMultipartUpload` envoie les parts droit vers
 * le stockage objet via des URLs pré-signées (docs/architecture.md §4). Ici on ne gère que
 * l'expérience — sélection du fichier, progression, rafraîchissement de la liste.
 */
/**
 * Bibliothèque de vidéos : téléversement, barre des sessions et grille. Partagée entre l'aperçu de
 * la page Data (`preview` : quelques rangées, puis une carte « Autres vidéos » qui mène à la page
 * dédiée) et la page pleine `/data/videos` (`full` : toutes les vidéos, sans carte de renvoi).
 */
function VideosLibrary({ variant }: { variant: 'preview' | 'full' }) {
  const t = useT();
  const { videos, loading, error, refresh } = useVideos();
  const { fieldSessions, refresh: refreshFieldSessions } = useFieldSessions();
  const captureAssets = useCaptureAssets();
  const { campaigns, refresh: refreshCampaigns } = useCampaigns();
  const { progress, upload, cancel } = useMultipartUpload();
  const [fieldSessionId, setFieldSessionId] = useState<string | null>(null);
  const [defaultCampaignId, setDefaultCampaignId] = useState<string | null>(null);
  const [canUpload, setCanUpload] = useState(false);

  // Rôle dans l'organisation active (la première adhésion, cf. store d'auth). Les actions
  // destructrices ou de partage sont masquées si le rôle ne les autorise pas — l'API reste la
  // source de vérité (elle renvoie 403/404), l'UI ne fait qu'éviter de proposer l'impossible.
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canDelete = role !== null && ROLE_RANK[role] >= ROLE_RANK.ADMIN;
  const canShare = role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;
  // Créer/modifier une session et y déplacer une vidéo : niveau éditeur (comme l'API).
  const canOrganize = role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;

  const [videoToDelete, setVideoToDelete] = useState<VideoListItem | null>(null);
  const [videoToShare, setVideoToShare] = useState<VideoListItem | null>(null);
  const [videoToMove, setVideoToMove] = useState<VideoListItem | null>(null);
  const [videoToAddToProject, setVideoToAddToProject] = useState<VideoListItem | null>(null);
  // Session de vol filtrant la grille (`null` = toutes), et session en cours d'édition/création.
  const [filterFieldSessionId, setFilterFieldSessionId] = useState<string | null>(null);
  // Appareil de captation filtrant la grille (`null` = tous), FR-20.
  const [filterAssetId, setFilterAssetId] = useState<string | null>(null);
  const [sessionToEdit, setSessionToEdit] = useState<FieldSession | 'new' | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        // Amorce une session de vol par défaut pour permettre le premier upload sans gestion manuelle
        // de l'arborescence Campagne › FieldSession. Réservé aux rôles EDITOR+ : un rôle en lecture
        // seule reçoit un 404 (la garde ne confirme pas la route), qu'on absorbe silencieusement.
        const fieldSession = await apiFetch<FieldSession>('/field-sessions/default', {
          method: 'POST',
        });
        if (active) {
          setFieldSessionId(fieldSession.id);
          // La campagne de la session par défaut sert de campagne d'accueil aux nouvelles sessions.
          setDefaultCampaignId(fieldSession.campaignId);
          setCanUpload(true);
        }
      } catch {
        if (active) {
          setCanUpload(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const onAddFiles = useCallback(
    async (_event: unknown, { addedFiles }: { addedFiles: File[] }) => {
      if (!fieldSessionId) {
        return;
      }
      // Séquentiel volontairement : le hook ne suit qu'un upload à la fois, et enchaîner évite de
      // saturer le lien montant avec plusieurs fichiers de plusieurs gigaoctets en parallèle.
      for (const file of addedFiles) {
        const videoId = await upload(file, fieldSessionId);
        if (videoId) {
          await refresh();
        }
      }
    },
    [fieldSessionId, upload, refresh],
  );

  const busy =
    progress.state === 'preparing' ||
    progress.state === 'uploading' ||
    progress.state === 'completing';

  // Grille filtrée par session et/ou appareil (filtre côté client : la liste complète est déjà
  // chargée, inutile de refaire un appel réseau par filtre). L'appareil d'une vidéo se déduit de sa
  // session de vol (ADR-0039) — les sessions portent `assetId`, la vidéo porte `fieldSessionId`.
  const assetBySession = new Map(fieldSessions.map((session) => [session.id, session.assetId]));
  const shownVideos = videos.filter(
    (video) =>
      (!filterFieldSessionId || video.fieldSessionId === filterFieldSessionId) &&
      (!filterAssetId || assetBySession.get(video.fieldSessionId) === filterAssetId),
  );

  // Densité (adaptée à l'écran) : l'aperçu n'affiche que quelques rangées, le reste derrière une
  // carte « Autres vidéos » qui mène à la page dédiée.
  const videosGridRef = useRef<HTMLUListElement>(null);
  const videosVisible = useVisibleCount(videosGridRef);
  // En aperçu (page Data) on ne montre que ce qui tient à l'écran ; « Autres vidéos » mène à la
  // page dédiée. En pleine page (/data/videos) on montre tout, sans carte de renvoi.
  const limitedVideos = variant === 'full' ? shownVideos : shownVideos.slice(0, videosVisible);
  const hiddenVideoCount = shownVideos.length - limitedVideos.length;

  const statusLabels: Record<VideoStatus, string> = {
    PENDING: t('data.status.pending'),
    UPLOADING: t('data.status.uploading'),
    UPLOADED: t('data.status.uploaded'),
    PROCESSING: t('data.status.processing'),
    READY: t('data.status.ready'),
    FAILED: t('data.status.failed'),
  };

  return (
    <>
      {variant === 'preview' && (
        <h2 id="data-videos" className="ddp-data__section-title">
          {t('data.section.videos')}
          <HelpHint textKey="help.data.videos" />
        </h2>
      )}

      {canUpload && (
        <div className="ddp-videos__uploader">
          <FileUploaderDropContainer
            accept={[...ACCEPTED_VIDEO_MIME_TYPES, ...ACCEPTED_VIDEO_EXTENSIONS]}
            labelText={t('data.upload.dropLabel')}
            multiple={false}
            disabled={busy || !fieldSessionId}
            onAddFiles={onAddFiles}
          />

          {busy && (
            <div className="ddp-videos__progress">
              <ProgressBar
                label={
                  progress.state === 'completing'
                    ? t('data.upload.finalizing')
                    : t('data.upload.uploading')
                }
                helperText={`${Math.round(progress.progress * 100)} %`}
                value={Math.round(progress.progress * 100)}
                max={100}
                status="active"
              />
              <Button kind="ghost" size="sm" onClick={() => void cancel()}>
                {t('data.action.cancel')}
              </Button>
            </div>
          )}

          {progress.state === 'error' && progress.error && (
            <InlineNotification
              kind="error"
              title={t('data.upload.errorTitle')}
              subtitle={progress.error}
              lowContrast
              hideCloseButton
            />
          )}
        </div>
      )}

      {error && (
        <InlineNotification
          kind="error"
          title={t('data.load.errorTitle')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      <FlightSessionsBar
        fieldSessions={fieldSessions}
        selectedId={filterFieldSessionId}
        onSelect={setFilterFieldSessionId}
        canOrganize={canOrganize}
        onNew={() => setSessionToEdit('new')}
        onEdit={(fieldSession) => setSessionToEdit(fieldSession)}
      />

      {captureAssets.length > 0 && (
        <div className="ddp-videos__filter">
          <Select
            id="filter-asset"
            size="sm"
            labelText={t('data.filter.asset')}
            value={filterAssetId ?? ''}
            onChange={(event) => setFilterAssetId(event.target.value || null)}
          >
            <SelectItem value="" text={t('data.filter.allAssets')} />
            {captureAssets.map((asset) => (
              <SelectItem key={asset.id} value={asset.id} text={asset.name} />
            ))}
          </Select>
        </div>
      )}

      {!loading && videos.length === 0 && !error && (
        <p className="ddp-videos__empty">{t('data.empty')}</p>
      )}

      {!loading && videos.length > 0 && shownVideos.length === 0 && (
        <p className="ddp-videos__empty">{t('data.emptyInSession')}</p>
      )}

      {shownVideos.length > 0 && (
        <ul className="ddp-videos__grid" ref={videosGridRef}>
          {limitedVideos.map((video) => (
            // Conteneur positionné : la carte est un lien, les actions sont des frères posés
            // par-dessus. Imbriquer des boutons dans un <a> serait du HTML invalide et capturerait
            // le clic destiné au lien.
            <li key={video.id} className="ddp-videos__cell">
              {/* Toute la carte est un lien : un clic n'importe où ouvre la page d'annotation. */}
              <RouterLink
                to="/videos/$videoId"
                params={{ videoId: video.id }}
                className="ddp-videos__card"
              >
                <VideoThumbnail
                  videoId={video.id}
                  status={video.status}
                  coverUrl={video.coverUrl}
                  className="ddp-videos__thumb"
                  mediaClassName="ddp-videos__thumb-media"
                  emptyClassName="ddp-videos__thumb-empty"
                />
                <div className="ddp-videos__card-top">
                  <span className="ddp-videos__title" title={video.title}>
                    {truncate(video.title, 42)}
                  </span>
                  <Tag
                    className="ddp-videos__status"
                    type={STATUS_META[video.status].type}
                    size="sm"
                  >
                    {statusLabels[video.status]}
                  </Tag>
                </div>
                <dl className="ddp-videos__meta">
                  <div>
                    <dt>{t('data.meta.file')}</dt>
                    <dd>{video.filename}</dd>
                  </div>
                  <div>
                    <dt>{t('data.meta.size')}</dt>
                    <dd>{formatBytes(video.sizeBytes)}</dd>
                  </div>
                  <div>
                    <dt>{t('data.meta.added')}</dt>
                    <dd>
                      {formatDate(video.createdAt)}
                      {video.uploadedByName ? ` · ${video.uploadedByName}` : ''}
                    </dd>
                  </div>
                  {video.assetName && (
                    <div>
                      <dt>{t('data.meta.asset')}</dt>
                      <dd>{video.assetName}</dd>
                    </div>
                  )}
                </dl>
                {video.labelSummary.length > 0 && (
                  <ul className="ddp-videos__labels">
                    {video.labelSummary.map((label) => (
                      <li key={label.name} className="ddp-videos__label">
                        <span
                          className="ddp-videos__label-dot"
                          style={{ backgroundColor: label.color }}
                        />
                        <span className="ddp-videos__label-name">{label.name}</span>
                        <span className="ddp-videos__label-count">{label.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </RouterLink>

              {(canShare || canDelete || canOrganize) && (
                <div className="ddp-videos__actions">
                  {canOrganize && (
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={FolderMoveTo}
                      iconDescription={t('data.action.moveToSession')}
                      tooltipPosition="top"
                      onClick={() => setVideoToMove(video)}
                    />
                  )}
                  {canOrganize && (
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={FolderAdd}
                      iconDescription={t('data.action.addToProject')}
                      tooltipPosition="top"
                      onClick={() => setVideoToAddToProject(video)}
                    />
                  )}
                  {canShare && (
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={Share}
                      iconDescription={t('data.action.share')}
                      tooltipPosition="top"
                      onClick={() => setVideoToShare(video)}
                    />
                  )}
                  {canDelete && (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={TrashCan}
                      iconDescription={t('data.action.delete')}
                      tooltipPosition="top"
                      onClick={() => setVideoToDelete(video)}
                    />
                  )}
                </div>
              )}
            </li>
          ))}
          {variant === 'preview' && hiddenVideoCount > 0 && (
            <li className="ddp-videos__cell">
              <RouterLink to="/data/videos" className="ddp-more-card">
                <span className="ddp-more-card__plus" aria-hidden="true">
                  +{hiddenVideoCount}
                </span>
                <span className="ddp-more-card__label">{t('data.more.videos')}</span>
                <span className="ddp-more-card__hint">{t('data.more.hint')}</span>
              </RouterLink>
            </li>
          )}
        </ul>
      )}

      {videoToDelete && (
        <DeleteVideoModal
          video={videoToDelete}
          onClose={() => setVideoToDelete(null)}
          onDeleted={async () => {
            setVideoToDelete(null);
            await refresh();
          }}
        />
      )}

      {videoToShare && (
        <ShareVideoModal video={videoToShare} onClose={() => setVideoToShare(null)} />
      )}

      {videoToAddToProject && (
        <AddToProjectModal
          target={{
            kind: 'video',
            ids: [videoToAddToProject.id],
            label: videoToAddToProject.title,
          }}
          onClose={() => setVideoToAddToProject(null)}
        />
      )}

      {sessionToEdit && defaultCampaignId && (
        <SessionModal
          fieldSession={sessionToEdit === 'new' ? null : sessionToEdit}
          campaigns={campaigns}
          captureAssets={captureAssets}
          defaultCampaignId={defaultCampaignId}
          onClose={() => setSessionToEdit(null)}
          onSaved={async () => {
            setSessionToEdit(null);
            await Promise.all([refreshFieldSessions(), refreshCampaigns()]);
          }}
        />
      )}

      {videoToMove && (
        <MoveVideoModal
          video={videoToMove}
          fieldSessions={fieldSessions}
          onClose={() => setVideoToMove(null)}
          onMoved={async () => {
            setVideoToMove(null);
            await Promise.all([refresh(), refreshFieldSessions()]);
          }}
        />
      )}
    </>
  );
}

/**
 * Page « Data » (hub, /data) : en-tête, sous-navigation accrochée (accessible depuis toute la page)
 * et les trois sections — vidéos, images, simulations — en aperçu. « Autres vidéos / images » mènent
 * aux pages dédiées `/data/videos` et `/data/images`.
 */
export function VideosPage() {
  const t = useT();
  return (
    <section className="ddp-page__section ddp-videos">
      <PageHeader title={t('data.title')} lead={t('data.subtitle')} tourScope="data" />

      {/* Liens de section horizontaux (ancre vers chaque bloc). La barre est accrochée en haut
          (sticky, cf. app.scss) : les liens restent atteignables pendant tout le défilement. */}
      <nav className="ddp-page-subnav" aria-label={t('data.title')}>
        <a href="#data-videos">{t('data.section.videos')}</a>
        <a href="#data-images">{t('data.section.images')}</a>
        <a href="#data-projects">{t('overview.projects.title')}</a>
        <a href="#data-simulation">{t('data.section.simulations')}</a>
      </nav>

      <VideosLibrary variant="preview" />

      <div id="data-images">
        <OrgImagesSection variant="preview" />
      </div>

      {/* Projets transverses : la création/gestion vit ici (l'Overview n'en montre qu'une liste). */}
      <ProjectsPanel anchorId="data-projects" />

      <div id="data-simulation">
        <SimulationSection />
      </div>
    </section>
  );
}

/**
 * Page dédiée « Vidéos » (/data/videos) : toutes les vidéos de l'organisation, sans troncature.
 * Même typographie et même en-tête que la page Data, avec un lien de retour.
 */
export function DataVideosPage() {
  const t = useT();
  return (
    <section className="ddp-page__section ddp-videos">
      <RouterLink to="/data" className="ddp-page-back">
        ← {t('data.title')}
      </RouterLink>
      <PageHeader title={t('data.section.videos')} lead={t('data.videosPage.subtitle')}>
        <RouterLink to="/data/videos/search" className="ddp-page-header__action">
          {t('search.open')}
        </RouterLink>
      </PageHeader>
      <VideosLibrary variant="full" />
    </section>
  );
}

/**
 * Page dédiée « Images » (/data/images) : toutes les images extraites de l'organisation, sans
 * troncature. Même typographie et même en-tête que la page Data, avec un lien de retour.
 */
export function DataImagesPage() {
  const t = useT();
  return (
    <section className="ddp-page__section ddp-videos">
      <RouterLink to="/data" className="ddp-page-back">
        ← {t('data.title')}
      </RouterLink>
      <PageHeader title={t('data.section.images')} lead={t('data.imagesPage.subtitle')} />
      <OrgImagesSection variant="full" />
    </section>
  );
}

/**
 * Barre des sessions de vol : un onglet « Toutes » plus une puce par session (avec son nombre de
 * vidéos), qui filtrent la grille. Un rôle éditeur peut créer une session et éditer chacune.
 */
function FlightSessionsBar({
  fieldSessions,
  selectedId,
  onSelect,
  canOrganize,
  onNew,
  onEdit,
}: {
  fieldSessions: FieldSession[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  canOrganize: boolean;
  onNew: () => void;
  onEdit: (fieldSession: FieldSession) => void;
}) {
  const t = useT();
  // Rien à montrer tant qu'aucune session n'existe et qu'on ne peut pas en créer.
  if (fieldSessions.length === 0 && !canOrganize) {
    return null;
  }

  return (
    <div className="ddp-sessions">
      <div className="ddp-sessions__head">
        <h2 className="ddp-sessions__title">{t('data.sessions.title')}</h2>
        {canOrganize && (
          <Button kind="ghost" size="sm" renderIcon={Add} onClick={onNew}>
            {t('data.sessions.new')}
          </Button>
        )}
      </div>
      <ul className="ddp-sessions__list">
        <li>
          <button
            type="button"
            className={`ddp-sessions__chip${selectedId === null ? ' is-selected' : ''}`}
            onClick={() => onSelect(null)}
          >
            <Folders size={16} />
            <span className="ddp-sessions__chip-name">{t('data.filter.all')}</span>
          </button>
        </li>
        {fieldSessions.map((fieldSession) => (
          <li key={fieldSession.id} className="ddp-sessions__item">
            <button
              type="button"
              className={`ddp-sessions__chip${selectedId === fieldSession.id ? ' is-selected' : ''}`}
              onClick={() => onSelect(fieldSession.id)}
              title={sessionSubtitle(fieldSession)}
            >
              <Folders size={16} />
              <span className="ddp-sessions__chip-name">{truncate(fieldSession.name, 28)}</span>
              <span className="ddp-sessions__chip-count">{fieldSession.videoCount}</span>
            </button>
            {canOrganize && (
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Edit}
                iconDescription={t('data.sessions.edit')}
                tooltipPosition="top"
                onClick={() => onEdit(fieldSession)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Résumé d'une session pour l'infobulle : appareil, lieu, date, contributeurs — ce qui est renseigné. */
function sessionSubtitle(fieldSession: FieldSession): string {
  const parts: string[] = [];
  if (fieldSession.assetName) parts.push(fieldSession.assetName);
  if (fieldSession.locationName) parts.push(fieldSession.locationName);
  if (fieldSession.flownAt) parts.push(formatDate(fieldSession.flownAt));
  if (fieldSession.contributors.length > 0) parts.push(fieldSession.contributors.join(', '));
  return parts.join(' · ');
}

/**
 * Création ou édition d'une session de vol. Une session vit sous une campagne : on la
 * choisit dans la liste ; à défaut, la campagne d'accueil. Les campagnes se créent dans « Images ».
 */
function SessionModal({
  fieldSession,
  campaigns,
  captureAssets,
  defaultCampaignId,
  onClose,
  onSaved,
}: {
  fieldSession: FieldSession | null;
  campaigns: Campaign[];
  captureAssets: CaptureAsset[];
  defaultCampaignId: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(fieldSession?.name ?? '');
  const [description, setDescription] = useState(fieldSession?.description ?? '');
  const [locationName, setLocationName] = useState(fieldSession?.locationName ?? '');
  const [flownAt, setFlownAt] = useState(
    fieldSession?.flownAt ? toDateInput(fieldSession.flownAt) : '',
  );
  const [contributors, setContributors] = useState(fieldSession?.contributors.join(', ') ?? '');
  const [campaignId, setCampaignId] = useState(fieldSession?.campaignId ?? defaultCampaignId);
  const [assetId, setAssetId] = useState(fieldSession?.assetId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        locationName: locationName.trim() || undefined,
        // `<input type="date">` rend une date locale « AAAA-MM-JJ » ; vide → non renseignée.
        flownAt: flownAt ? new Date(flownAt).toISOString() : undefined,
        // Contributeurs saisis séparés par des virgules, nettoyés des entrées vides.
        contributors: contributors
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0),
        campaignId,
        // Appareil rattaché : `null` dissocie explicitement (ADR-0039) ; une valeur rattache.
        assetId: assetId || null,
      };
      if (fieldSession) {
        await apiFetch(`/field-sessions/${fieldSession.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/field-sessions', { method: 'POST', body });
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.saveFailed'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="sm"
      modalHeading={fieldSession ? t('data.session.editHeading') : t('data.session.newHeading')}
      primaryButtonText={busy ? t('data.action.saving') : t('data.action.save')}
      secondaryButtonText={t('data.action.cancel')}
      primaryButtonDisabled={busy || name.trim().length < 2}
      onRequestClose={onClose}
      onRequestSubmit={() => void onSubmit()}
    >
      <div className="ddp-session-form">
        <p className="ddp-session-form__hint">{t('data.session.hint')}</p>
        <TextInput
          id="session-name"
          labelText={t('data.field.name')}
          placeholder={t('data.session.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        {campaigns.length > 0 && (
          <Select
            id="session-campaign"
            labelText={t('data.field.campaign')}
            helperText={t('data.session.campaignHelper')}
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
          >
            {campaigns.map((campaign) => (
              <SelectItem key={campaign.id} value={campaign.id} text={campaign.name} />
            ))}
          </Select>
        )}
        {captureAssets.length > 0 && (
          <Select
            id="session-asset"
            labelText={t('data.field.asset')}
            helperText={t('data.session.assetHelper')}
            value={assetId}
            onChange={(event) => setAssetId(event.target.value)}
          >
            <SelectItem value="" text={t('data.session.assetNone')} />
            {captureAssets.map((asset) => (
              <SelectItem key={asset.id} value={asset.id} text={asset.name} />
            ))}
          </Select>
        )}
        <TextArea
          id="session-description"
          labelText={t('data.field.description')}
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <TextInput
          id="session-location"
          labelText={t('data.field.location')}
          placeholder={t('data.session.locationPlaceholder')}
          value={locationName}
          onChange={(event) => setLocationName(event.target.value)}
        />
        <TextInput
          id="session-date"
          type="date"
          labelText={t('data.field.flightDate')}
          value={flownAt}
          onChange={(event) => setFlownAt(event.target.value)}
        />
        <TextInput
          id="session-contributors"
          labelText={t('data.field.contributors')}
          helperText={t('data.session.contributorsHelper')}
          placeholder={t('data.session.contributorsPlaceholder')}
          value={contributors}
          onChange={(event) => setContributors(event.target.value)}
        />
        {error && (
          <InlineNotification
            kind="error"
            title={t('data.error.title')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}

/** Déplace une vidéo vers une autre session de vol. Choix par liste de cases (peu de sessions). */
function MoveVideoModal({
  video,
  fieldSessions,
  onClose,
  onMoved,
}: {
  video: VideoListItem;
  fieldSessions: FieldSession[];
  onClose: () => void;
  onMoved: () => Promise<void>;
}) {
  const t = useT();
  const [target, setTarget] = useState<string>(video.fieldSessionId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    if (target === video.fieldSessionId) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/videos/${video.id}`, { method: 'PATCH', body: { fieldSessionId: target } });
      await onMoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.moveFailed'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="sm"
      modalHeading={t('data.move.heading')}
      primaryButtonText={busy ? t('data.move.moving') : t('data.action.move')}
      secondaryButtonText={t('data.action.cancel')}
      primaryButtonDisabled={busy || fieldSessions.length === 0}
      onRequestClose={onClose}
      onRequestSubmit={() => void onSubmit()}
    >
      <div className="ddp-session-form">
        <p className="ddp-session-form__hint">« {truncate(video.title, 60)} »</p>
        {fieldSessions.length === 0 ? (
          <p>{t('data.move.noSessions')}</p>
        ) : (
          <RadioButtonGroup
            name="move-session"
            orientation="vertical"
            valueSelected={target}
            onChange={(value) => setTarget(String(value))}
          >
            {fieldSessions.map((fieldSession) => (
              <RadioButton
                key={fieldSession.id}
                labelText={`${fieldSession.name} (${fieldSession.videoCount})`}
                value={fieldSession.id}
              />
            ))}
          </RadioButtonGroup>
        )}
        {error && (
          <InlineNotification
            kind="error"
            title={t('data.error.title')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}

/** Rend une date pour un `<input type="date">` (AAAA-MM-JJ, en heure locale). */
function toDateInput(value: Date): string {
  const d = new Date(value);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Confirmation de suppression : geste irréversible côté produit, donc jamais en un seul clic. */
function DeleteVideoModal({
  video,
  onClose,
  onDeleted,
}: {
  video: VideoListItem;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/videos/${video.id}`, { method: 'DELETE' });
      await onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.deleteFailed'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      danger
      size="xs"
      modalHeading={t('data.delete.heading')}
      primaryButtonText={busy ? t('data.delete.deleting') : t('data.action.delete')}
      secondaryButtonText={t('data.action.cancel')}
      primaryButtonDisabled={busy}
      onRequestClose={onClose}
      onRequestSubmit={() => void onConfirm()}
    >
      <p>
        {t('data.delete.confirmPrefix')} « {video.title} » {t('data.delete.confirmSuffix')}
      </p>
      {error && (
        <InlineNotification
          kind="error"
          title={t('data.error.title')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
    </Modal>
  );
}

/** Génère un lien de partage et le présente, prêt à copier. Le jeton n'est visible qu'ici. */
function ShareVideoModal({ video, onClose }: { video: VideoListItem; onClose: () => void }) {
  const t = useT();
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [includeAnnotations, setIncludeAnnotations] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<ShareLink | null>(null);
  const [copied, setCopied] = useState(false);

  const onCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const days = Number.parseInt(expiresInDays, 10);
      const created = await apiFetch<ShareLink>('/shares', {
        method: 'POST',
        body: {
          scope: 'VIDEO',
          resourceId: video.id,
          expiresInDays: Number.isFinite(days) && days > 0 ? days : 30,
          includeAnnotations,
          allowDownload,
        },
      });
      setLink(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.shareCreateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // Le presse-papiers peut être refusé (permissions) : l'URL reste sélectionnable à la main.
      setCopied(false);
    }
  };

  return (
    <Modal
      open
      modalHeading={t('data.share.heading')}
      primaryButtonText={
        link ? t('data.share.done') : busy ? t('data.share.creating') : t('data.share.create')
      }
      secondaryButtonText={t('data.action.cancel')}
      primaryButtonDisabled={busy}
      onRequestClose={onClose}
      onRequestSubmit={() => (link ? onClose() : void onCreate())}
    >
      {!link ? (
        <div className="ddp-share-form">
          <p>
            {t('data.share.introPrefix')} « {video.title} », {t('data.share.introSuffix')}
          </p>
          <TextInput
            id="share-expiry"
            type="number"
            min={1}
            max={365}
            labelText={t('data.share.expiryLabel')}
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
          />
          <Checkbox
            id="share-annotations"
            labelText={t('data.share.includeAnnotations')}
            checked={includeAnnotations}
            onChange={(_event, { checked }) => setIncludeAnnotations(checked)}
          />
          <Checkbox
            id="share-download"
            labelText={t('data.share.allowDownload')}
            checked={allowDownload}
            onChange={(_event, { checked }) => setAllowDownload(checked)}
          />
          {error && (
            <InlineNotification
              kind="error"
              title={t('data.error.title')}
              subtitle={error}
              lowContrast
              hideCloseButton
            />
          )}
        </div>
      ) : (
        <div className="ddp-share-form">
          <p>{t('data.share.created')}</p>
          <div className="ddp-share-form__link">
            <TextInput
              id="share-url"
              labelText={t('data.share.linkLabel')}
              value={link.url}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button
              kind="tertiary"
              size="md"
              hasIconOnly
              renderIcon={Copy}
              iconDescription={copied ? t('data.action.copied') : t('data.action.copy')}
              tooltipPosition="left"
              onClick={() => void onCopy()}
            />
          </div>
          <p className="ddp-share-form__expiry">
            {t('data.share.validUntil')} {formatDate(link.expiresAt)}.
          </p>
        </div>
      )}
    </Modal>
  );
}

/**
 * Section « Images » de l'accueil : toutes les images extraites de l'organisation, associées à
 * leur vidéo source.
 *
 * Cliquer une vignette la **sélectionne** (elle n'ouvre plus la vidéo — c'est le titre en dessous
 * qui y mène) ; maintenir le bouton et survoler d'autres vignettes les sélectionne d'un trait
 * (« peinture »). Le lot sélectionné s'exporte en `.zip` au format Ultralytics YOLO (FR-28).
 */
function OrgImagesSection({ variant }: { variant: 'preview' | 'full' }) {
  const t = useT();
  // Section autonome (utilisée en aperçu sur Data et en pleine page sur /data/images) : elle lit
  // elle-même le rôle et les campagnes, sans dépendre de la page hôte.
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canOrganize = role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;
  const { campaigns, refresh: refreshCampaigns } = useCampaigns();
  const [images, setImages] = useState<ExtractedImage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Campagne filtrant la galerie (`null` = toutes), et campagne en cours d'édition.
  const [filterCampaignId, setFilterCampaignId] = useState<string | null>(null);
  const [campaignToEdit, setCampaignToEdit] = useState<Campaign | 'new' | null>(null);
  // Ouvre la modale « Ajouter au projet… » pour le lot d'images sélectionnées (EDITOR+).
  const [addToProjectOpen, setAddToProjectOpen] = useState(false);
  // Densité (adaptée à l'écran). Hooks déclarés ici, avant tout retour anticipé (règle des hooks).
  const imagesGridRef = useRef<HTMLUListElement>(null);
  const imagesVisible = useVisibleCount(imagesGridRef);

  // Peinture : un glisser (bouton maintenu) applique le même geste — sélection ou désélection —
  // à chaque vignette survolée. Le mode est fixé au premier contact, dans une ref pour ne pas
  // relancer les gestionnaires à chaque frame.
  const painting = useRef(false);
  const paintMode = useRef<'select' | 'deselect'>('select');

  useEffect(() => {
    let active = true;
    apiFetch<ExtractedImage[]>('/images')
      .then((list) => {
        if (active) {
          setImages(list);
          setLoaded(true);
        }
      })
      // Rôle sans accès ou aucune image : on masque simplement la section.
      .catch(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Le glisser se termine où qu'on relâche, y compris hors d'une vignette.
  useEffect(() => {
    const stop = () => {
      painting.current = false;
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, []);

  const applyPaint = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (paintMode.current === 'select') next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const onPointerDown = useCallback(
    (id: string, isSelected: boolean) => (event: React.PointerEvent) => {
      if (event.button !== 0) return; // bouton gauche uniquement
      painting.current = true;
      paintMode.current = isSelected ? 'deselect' : 'select';
      applyPaint(id);
    },
    [applyPaint],
  );

  const onPointerEnter = useCallback(
    (id: string) => () => {
      if (painting.current) applyPaint(id);
    },
    [applyPaint],
  );

  const onDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      // Chargé à la demande : garde `fflate` et le code d'archive hors du bundle initial (NFR-11).
      const { downloadImagesAsYolo } = await import('../features/images/yolo-download.js');
      await downloadImagesAsYolo([...selected]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.exportFailed'));
    } finally {
      setDownloading(false);
    }
  };

  if (!loaded || images.length === 0) {
    return null;
  }

  // Galerie filtrée par campagne (côté client : la liste complète est déjà chargée).
  const shownImages = filterCampaignId
    ? images.filter((img) => img.campaignId === filterCampaignId)
    : images;
  const count = selected.size;
  const allShownSelected =
    shownImages.length > 0 && shownImages.every((img) => selected.has(img.id));

  // En aperçu : quelques rangées, le reste derrière « Autres images » qui mène à /data/images.
  // En pleine page : toutes les images.
  const limitedImages = variant === 'full' ? shownImages : shownImages.slice(0, imagesVisible);
  const hiddenImageCount = shownImages.length - limitedImages.length;

  return (
    <section className="ddp-images">
      <h2 className="ddp-data__section-title">
        {t('data.images.title')} ({images.length})
        <HelpHint textKey="help.data.images" />
      </h2>
      <p className="ddp-images__hint">{t('data.images.hint')}</p>

      <CampaignsBar
        campaigns={campaigns}
        selectedId={filterCampaignId}
        onSelect={setFilterCampaignId}
        canOrganize={canOrganize}
        onNew={() => setCampaignToEdit('new')}
        onEdit={(campaign) => setCampaignToEdit(campaign)}
      />

      <div className="ddp-images__toolbar" role="toolbar" aria-label={t('data.images.toolbarAria')}>
        <span className="ddp-images__toolbar-count">
          {count > 0 ? `${count} ${t('data.images.selectedCount')}` : t('data.images.noSelection')}
        </span>
        <Button
          kind="ghost"
          size="sm"
          disabled={allShownSelected}
          onClick={() => setSelected(new Set(shownImages.map((i) => i.id)))}
        >
          {t('data.images.selectAll')}
        </Button>
        <Button
          kind="ghost"
          size="sm"
          disabled={count === 0}
          onClick={() => setSelected(new Set())}
        >
          {t('data.images.deselectAll')}
        </Button>
        {canOrganize && (
          <>
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={FolderAdd}
              disabled={count === 0}
              onClick={() => setAddToProjectOpen(true)}
            >
              {t('data.addToProject.add')}
            </Button>
            <HelpHint textKey="help.data.addToProject" />
          </>
        )}
        <Button
          kind="primary"
          size="sm"
          renderIcon={Download}
          disabled={count === 0 || downloading}
          onClick={() => void onDownload()}
        >
          {downloading
            ? t('data.images.preparing')
            : `${t('data.images.download')} (${count}) ${t('data.images.inYolo')}`}
        </Button>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          title={t('data.images.exportErrorTitle')}
          subtitle={error}
          lowContrast
          onCloseButtonClick={() => setError(null)}
        />
      )}

      <ul className="ddp-images__grid" ref={imagesGridRef}>
        {limitedImages.map((img) => {
          const isSelected = selected.has(img.id);
          return (
            <li key={img.id} className={`ddp-images__cell${isSelected ? ' is-selected' : ''}`}>
              <button
                type="button"
                className="ddp-images__select"
                aria-pressed={isSelected}
                aria-label={`${t('data.images.selectImageOf')} ${img.videoTitle}`}
                onPointerDown={onPointerDown(img.id, isSelected)}
                onPointerEnter={onPointerEnter(img.id)}
              >
                <div className="ddp-images__thumb">
                  <ThumbImage url={img.thumbUrl ?? img.url} />
                  {img.annotationCount > 0 && (
                    <span className="ddp-images__badge">{img.annotationCount}</span>
                  )}
                  <span className="ddp-images__check" aria-hidden="true">
                    {isSelected && <Checkmark size={16} />}
                  </span>
                </div>
              </button>
              <RouterLink
                to="/videos/$videoId"
                params={{ videoId: img.videoId }}
                className="ddp-images__video-link"
                title={img.videoTitle}
              >
                {truncate(img.videoTitle, 32)}
              </RouterLink>
              <span className="ddp-images__meta">
                {formatDate(img.createdAt)}
                {img.createdByName ? ` · ${img.createdByName}` : ''}
              </span>
            </li>
          );
        })}
        {variant === 'preview' && hiddenImageCount > 0 && (
          <li className="ddp-images__cell">
            <RouterLink to="/data/images" className="ddp-more-card ddp-more-card--image">
              <span className="ddp-more-card__plus" aria-hidden="true">
                +{hiddenImageCount}
              </span>
              <span className="ddp-more-card__label">{t('data.more.images')}</span>
              <span className="ddp-more-card__hint">{t('data.more.hint')}</span>
            </RouterLink>
          </li>
        )}
      </ul>

      {campaignToEdit && (
        <CampaignModal
          campaign={campaignToEdit === 'new' ? null : campaignToEdit}
          onClose={() => setCampaignToEdit(null)}
          onSaved={async () => {
            setCampaignToEdit(null);
            await refreshCampaigns();
          }}
        />
      )}

      {addToProjectOpen && (
        <AddToProjectModal
          target={{
            kind: 'image',
            ids: [...selected],
            label: `${count} ${t('data.images.selectedCount')}`,
          }}
          onClose={() => setAddToProjectOpen(false)}
        />
      )}
    </section>
  );
}

/**
 * Barre des campagnes de la section « Images » : un onglet « Toutes » plus une puce par
 * campagne, qui filtrent la galerie. Un rôle éditeur peut créer une campagne et éditer chacune.
 * Réutilise l'habillage « dossier » de la barre des sessions de vol.
 */
function CampaignsBar({
  campaigns,
  selectedId,
  onSelect,
  canOrganize,
  onNew,
  onEdit,
}: {
  campaigns: Campaign[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  canOrganize: boolean;
  onNew: () => void;
  onEdit: (campaign: Campaign) => void;
}) {
  const t = useT();
  if (campaigns.length === 0 && !canOrganize) {
    return null;
  }

  return (
    <div className="ddp-sessions">
      <div className="ddp-sessions__head">
        <h3 className="ddp-sessions__title">
          {t('data.projects.title')}
          <HelpHint textKey="help.data.campaigns" />
        </h3>
        {canOrganize && (
          <Button kind="ghost" size="sm" renderIcon={Add} onClick={onNew}>
            {t('data.projects.new')}
          </Button>
        )}
      </div>
      <ul className="ddp-sessions__list">
        <li>
          <button
            type="button"
            className={`ddp-sessions__chip${selectedId === null ? ' is-selected' : ''}`}
            onClick={() => onSelect(null)}
          >
            <Folders size={16} />
            <span className="ddp-sessions__chip-name">{t('data.filter.all')}</span>
          </button>
        </li>
        {campaigns.map((campaign) => (
          <li key={campaign.id} className="ddp-sessions__item">
            <button
              type="button"
              className={`ddp-sessions__chip${selectedId === campaign.id ? ' is-selected' : ''}`}
              onClick={() => onSelect(campaign.id)}
              title={campaign.description ?? undefined}
            >
              <Folders size={16} />
              <span className="ddp-sessions__chip-name">{truncate(campaign.name, 28)}</span>
            </button>
            {canOrganize && (
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Edit}
                iconDescription={t('data.projects.edit')}
                tooltipPosition="top"
                onClick={() => onEdit(campaign)}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Création ou édition d'une campagne : nom et description. */
function CampaignModal({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: Campaign | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(campaign?.name ?? '');
  const [description, setDescription] = useState(campaign?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), description: description.trim() || undefined };
      if (campaign) {
        await apiFetch(`/campaigns/${campaign.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/campaigns', { method: 'POST', body });
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('data.error.saveFailed'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      size="sm"
      modalHeading={campaign ? t('data.project.editHeading') : t('data.project.newHeading')}
      primaryButtonText={busy ? t('data.action.saving') : t('data.action.save')}
      secondaryButtonText={t('data.action.cancel')}
      primaryButtonDisabled={busy || name.trim().length < 2}
      onRequestClose={onClose}
      onRequestSubmit={() => void onSubmit()}
    >
      <div className="ddp-session-form">
        <p className="ddp-session-form__hint">{t('data.project.hint')}</p>
        <TextInput
          id="project-name"
          labelText={t('data.field.name')}
          placeholder={t('data.project.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <TextArea
          id="project-description"
          labelText={t('data.field.description')}
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        {error && (
          <InlineNotification
            kind="error"
            title={t('data.error.title')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * Vignette d'image extraite. Elle n'apparaît qu'une fois décodée : sans cela, certaines images
 * restaient noires jusqu'à ce qu'un survol force un repaint. Le passage de `loaded` à `true`
 * (après `onLoad`) déclenche un rendu, donc une peinture, indépendamment du survol.
 *
 * `draggable={false}` : sans quoi un glisser de sélection démarrerait le drag-image natif du
 * navigateur (image fantôme) au lieu de peindre la sélection.
 */
function ThumbImage({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={url}
      alt=""
      className={`ddp-images__thumb-media${loaded ? ' is-loaded' : ''}`}
      decoding="async"
      loading="lazy"
      draggable={false}
      onLoad={() => setLoaded(true)}
    />
  );
}

/** Tronque un titre trop long pour la carte (le titre complet reste en infobulle). */
function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  const units = ['Kio', 'Mio', 'Gio', 'Tio'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
